<template>
  <DefaultLayout v-if="!isLoading && showAuditPage">
    <template>
      <AuditHeader
        :auditDetail="auditDetail"
        :auditSettingInfo="auditSettingInfo"
        :dataCollectorRole="true"
      />
      <br />
      <article
        v-if="isDataCollectionArchived"
        class="tile notification bg-primary-light p1"
      >
        <small>{{ message }}</small>
      </article>
      <b-tabs
        @change="tabChange"
        :class="$style.tabLabel"
        v-model="currentTab"
        :animated="false"
        :destroy-on-hide="true"
      >
        <b-tab-item :class="$style.tabItem" label="DATA COLLECTION SHEET">
          <DataCollectionSheet
            :auditDetail="auditDetail"
            :auditId="auditId"
            @refreshDataCollectionSheet="refreshDataCollectionSheet"
            v-if="
              showDataCollectionSheet &&
              isDataCollectionActive &&
              !isDataCollectionArchived &&
              isDisplayDataCollection
            "
            :key="dataCollectionSheetTabKey"
          />
          <Card
            v-if="!isDataCollectionActive || isDataCollectionArchived"
            :class="$style.dataCollectionInactive"
          >
            <div>
              <b-icon icon="alert-circle-outline" size="is-medium"></b-icon>
            </div>
            <span :class="$style.headerText">Record Submissions Disabled</span>
            <span :class="$style.substituteText"
              >Records cannot be submitted because the project administrator has
              stopped data collection.</span
            >
          </Card>
          <!-- A data collector cannot enter data if the cycle is not current -->
          <Card
            v-else-if="
              auditDetail.endDate && dateToday > new Date(auditDetail.endDate)
            "
            :class="$style.dataCollectionInactive"
          >
            <div>
              <b-icon icon="alert-circle-outline" size="is-medium"></b-icon>
            </div>
            <span :class="$style.headerText">Record Submissions Disabled</span>
            <span :class="$style.substituteText"
              >Records cannot be submitted for former data collection
              period.</span
            >
          </Card>
        </b-tab-item>
        <b-tab-item :class="$style.tabItem" label="DATA HISTORY">
          <DataHistory :auditDetail="auditDetail" :auditId="auditId" />
        </b-tab-item>
        <b-tab-item :class="$style.tabItem" label="GRIP" v-if="checkGripStatus">
          <GripTab
            :isDataCollector="true"
            :projectId="auditDetail.project.id"
            :projectDetail="auditDetail.project"
            :class="$style.gripTab"
          ></GripTab>
        </b-tab-item>
      </b-tabs>
    </template>
  </DefaultLayout>
</template>
<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import { AuditDetail } from '@/store/modules/audits/types/audits.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import AuditHeader from '@/views/Audit/components/AuditHeader.vue';
import DataCollectionSheet from '@/views/Audit/components/dataCollector/DataCollectionSheet.vue';
import DataHistory from '@/views/Audit/components/dataCollector/DataHistory.vue';
import { cloneDeep as _cloneDeep } from 'lodash';
import { useUserEmail } from '@/utils/user.util';
import Card from '@/components/Card.vue';
import { AuditStatus } from '@/store/modules/audits/types/audits.types';
import GripTab from '../../Grip/GripTab.vue';
import { UserRoleInAudit } from '@/store/modules/projects/types/projects.types';
import { handleErrorResponse } from '@/utils/api-response-error-handler.util';

/**
 * Declaration of available tabs in the page
 */
const tabNames = ['#dataCollectionSheet', '#dataHistory', '#grip'];

@Component({
  components: {
    DefaultLayout,
    HeroBar,
    AuditHeader,
    DataCollectionSheet,
    DataHistory,
    Card,
    GripTab,
  },
})
export default class AuditPageDataCollector extends Vue {
  public currentTab = 0;
  public showDataCollectionSheet: boolean = false;
  public showDataHistoryTab: boolean = false;
  public showGripTab: boolean = false;
  public dataCollectionSheetTabKey: number = Math.floor(Math.random() * 999);
  public dataHistoryTabKey: number = Math.floor(Math.random() * 999);
  public gripTabKey: number = Math.floor(Math.random() * 999);
  public isDataCollectionActive: boolean = false;
  public isDataCollectionArchived: boolean = false;
  public showAuditPage: boolean = false;
  public auditSettingInfo: any = [];
  public dateToday: Date = new Date();
  public isLoading: boolean = true;
  public isDisplayDataCollection: boolean = false;

  @Action('audits/getAuditDetail')
  public getAuditDetail!: (auditId: number) => void;

  @Action('audits/getUpdatedAuditJBICriteriaList')
  public getUpdatedAuditJBICriteriaList!: (auditId: number) => void;

  @State((state) => state.audits.auditDetail)
  public auditDetail!: AuditDetail;

  @State((state) => state.audits.apiState.getAuditDetail.success)
  public getAuditDetailApiStateSuccess!: boolean;

  public mounted(): void {
    this.changeActiveTabBasedOnUrl();
    this.getUpdatedAuditJBICriteriaList(this.auditId);

    try {
      this.getAuditDetail(this.auditId);
    } catch (error) {
      handleErrorResponse(error, this);
      return;
    }
  }

  public changeActiveTabBasedOnUrl() {
    const tabIndex = Object.values(tabNames).findIndex(
      (value) => value === this.currentRouteHash,
    );
    this.currentTab = tabIndex !== -1 ? tabIndex : 0;
    this.renderTabComponent();
  }

  public tabChange() {
    this.isLoading = true;
    this.getAuditDetail(this.auditId);
    this.$router.replace({ hash: tabNames[this.currentTab] });
    this.renderTabComponent();
  }

  public renderTabComponent() {
    switch (tabNames[this.currentTab]) {
      case '#dataCollectionSheet':
        this.showGripTab = false;
        this.showDataHistoryTab = false;
        this.showDataCollectionSheet = true;
        this.dataCollectionSheetTabKey += 1;
        break;
      case '#dataHistory':
        this.showDataCollectionSheet = false;
        this.showGripTab = false;
        this.showDataHistoryTab = true;
        this.dataHistoryTabKey += 1;
        break;
      case '#grip':
        this.showDataCollectionSheet = false;
        this.showDataHistoryTab = false;
        this.showGripTab = true;
        this.gripTabKey += 1;
        break;
      default:
        break;
    }
  }

  get auditId(): number {
    return +this.$route.params.auditId;
  }

  get currentRouteHash() {
    return this.$route.hash;
  }

  get checkGripStatus() {
    return this.auditDetail.enableGrip;
  }

  public refreshDataCollectionSheet() {
    this.renderTabComponent();
  }

  @Watch('getAuditDetailApiStateSuccess')
  @isTruthy
  public watchGetAuditDetailSuccess() {
    /** If audit is deleted or
     * user role is not "Data Collector" in audit
     * return back to prev page or project list
     */
    if (
      this.auditDetail.status === 'Deleted' ||
      this.auditDetail.userRoleInAudit !== UserRoleInAudit.isDataCollector
    ) {
      if (this.auditDetail.project) {
        /**
         * if no history routes redirect to project list
         * else redirect 1 step back
         */
        if (window.history.state === null) {
          this.$router.push({
            name: 'project-me',
          });
        } else {
          this.$router.go(-1);
        }
      }
    }
    this.auditSettingInfo = {
      name: this.auditDetail.name,
      description: this.auditDetail.description,
      enableGrip: this.auditDetail.enableGrip,
    };
    this.isDataCollectionActive = this.auditDetail.endedAt ? false : true;
    this.isDataCollectionArchived =
      this.auditDetail.status === AuditStatus.ARCHIVED ? true : false;
    this.isDisplayDataCollection =
      this.auditDetail.auditCategory === 'Cycle'
        ? this.dateToday < new Date(this.auditDetail.endDate)
        : true;
    this.isLoading = false;
    this.showAuditPage = true;
  }

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  get message() {
    return 'This data collection period has been archived';
  }
}
</script>
<style lang="scss" module>
.tabLabel nav li a {
  padding-left: 0;
  color: #9f9f9f;
  font-size: 16px;
  font-weight: 700;
  padding-right: 0px;
}
.tabLabel nav li a:hover {
  color: #00205b;
}
.tabLabel nav li {
  padding-right: 2rem;
}
.tabLabel > section {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.gripTab {
  margin-top: 24px;
}
.dataCollectionInactive {
  margin-top: 36px;
  div {
    margin-bottom: 20px;
  }
  display: flex;
  flex-direction: column;
  background: #ffffff;
  font-size: 18px;
  color: #9f9f9f;
  padding: 5rem;
  box-shadow: 0 0 4px 0 #dbdbdb;
  text-align: center;
  .headerText {
    font-weight: bold;
    margin-bottom: 6px;
  }
  .substituteText {
    font-weight: 400;
  }
}
</style>
